// src/resources/Letters/MDLetterSign.js
import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { fetchLetterAndCompany, handleSignAndFinalize } from "./MDLetterUtils";
import MDLetterActions from "./MDLetterActions";

export const MDLetterSign = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const letterRef = useRef(null);

  const [letter, setLetter] = useState(null);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [done, setDone] = useState(false);

  const [dragPos, setDragPos] = useState({ x: 400, y: 700 });
  const [isDragging, setIsDragging] = useState(false);

  const userEmail = (localStorage.getItem("email") || "").toLowerCase();
  const userRole = (localStorage.getItem("role") || "").toLowerCase();

  const isMD =
    userRole === "md" ||
    userEmail === "abdulla@cll.ae" ||
    userEmail.includes("md@") ||
    userEmail.includes("managingdirector");

  /* -------------------------------------------------------
     1️⃣ FETCH LETTER + COMPANY
  ------------------------------------------------------- */
  useEffect(() => {
    fetchLetterAndCompany(id, setLetter, setCompany, setLoading);
  }, [id]);

  /* -------------------------------------------------------
     2️⃣ AUTO-OPEN SIGNED PDF (MUST BE BEFORE ANY RETURN)
  ------------------------------------------------------- */
  useEffect(() => {
    if (letter && letter.status === "signed" && letter.signedPdfUrl) {
      window.open(letter.signedPdfUrl, "_blank");
    }
  }, [letter]);

  /* -------------------------------------------------------
     3️⃣ CONDITIONAL RETURNS
  ------------------------------------------------------- */
  if (loading) {
    return <p style={{ padding: 40 }}>⏳ Loading letter...</p>;
  }

  if (!letter) {
    return <p style={{ padding: 40 }}>⚠️ Letter not found.</p>;
  }

  // If already signed → show simple preview page
  if (letter.status === "signed" && letter.signedPdfUrl) {
    return (
      <div style={{ padding: 40, textAlign: "center" }}>
        <h2>Signed Letter</h2>
        <p>This letter has already been finalized and signed.</p>

        <a
          href={letter.signedPdfUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: "inline-block",
            marginTop: "20px",
            padding: "10px 18px",
            background: "#4f46e5",
            color: "#fff",
            borderRadius: 6,
            textDecoration: "none",
            fontWeight: 600,
          }}
        >
          🔍 View Signed PDF Again
        </a>

        <div style={{ marginTop: "30px" }}>
          <a href="/signedLetters">⬅ Back to Signed Letters List</a>
        </div>
      </div>
    );
  }

  /* -------------------------------------------------------
     4️⃣ MD SIGN LOGIC
  ------------------------------------------------------- */
  const status = (letter.status || "pending").toLowerCase();

  let pageTitle = "📄 Letter Preview";
  if (status === "signed") pageTitle = "✅ Signed Letter";
  else if (isMD && status === "pending") pageTitle = "✍️ Review & Sign Letter";
  else if (!isMD && status === "pending") pageTitle = "🕓 Awaiting MD Signature";

  const handleSign = async () => {
    if (!isMD) return;
    setSigning(true);
    await handleSignAndFinalize({
      letterNode: letterRef.current,
      letter,
      id,
      setDone,
      navigate,
    });
    setSigning(false);
  };

  /* -------------------------------------------------------
     5️⃣ SIGNATURE DRAG EVENTS
  ------------------------------------------------------- */
  const handleMouseDown = (e) => {
    if (!isMD) return;
    e.preventDefault();
    setIsDragging(true);
  };

  const handleMouseUp = () => setIsDragging(false);

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    const rect = letterRef.current.getBoundingClientRect();
    setDragPos({
      x: e.clientX - rect.left - 90,
      y: e.clientY - rect.top - 45,
    });
  };

  /* -------------------------------------------------------
     6️⃣ STYLES
  ------------------------------------------------------- */
  const previewWrapperStyle = {
    position: "relative",
    marginTop: 20,
    border: "1px solid #ccc",
    borderRadius: 10,
    overflow: "hidden",
    height: 842,
    width: 595,
    background: "#fff",
    marginLeft: "auto",
    marginRight: "auto",
    boxShadow: "0 0 10px rgba(0,0,0,0.1)",
    cursor: isDragging ? "grabbing" : "default",
  };

  const contentBoxStyle = {
    position: "absolute",
    top: "120px",
    left: "60px",
    right: "60px",
    bottom: "80px",
    zIndex: 2,
    fontFamily:
      letter.direction === "rtl"
        ? "'Noto Naskh Arabic','Amiri',serif"
        : "Calibri, Arial, sans-serif",
    fontSize: "10pt",
    color: "#000",
    lineHeight: 1.6,
    textAlign: letter.direction === "rtl" ? "right" : "justify",
    direction: letter.direction || "ltr",
    overflowY: "auto",
  };

  /* -------------------------------------------------------
     7️⃣ FINAL RENDER (PENDING SIGNATURE FLOW)
  ------------------------------------------------------- */
  return (
    <div
      style={{
        padding: 40,
        background: "#f7f9fb",
        minHeight: "100vh",
      }}
    >
      <h2 style={{ textAlign: "center" }}>{pageTitle}</h2>

      <div
        ref={letterRef}
        style={previewWrapperStyle}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
      >
        {company?.letterheadUrl && (
  <img
    src={company.letterheadUrl}
    alt="Letterhead"
    crossOrigin="anonymous"
    referrerPolicy="no-referrer"
    style={{
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      objectFit: "cover",
      zIndex: 0,
    }}
  />
)}


        <div style={contentBoxStyle}>
          <div style={{ marginBottom: "8px", lineHeight: "1.3" }}>
            <div>
              <strong>Ref:</strong> {letter.referenceNo || "Auto"}
            </div>
            <div>
              <strong>Date:</strong>{" "}
              {letter.date
                ? new Date(letter.date).toLocaleDateString("en-GB")
                : new Date().toLocaleDateString("en-GB")}
            </div>
          </div>

          {letter.subject && (
            <h4
              style={{
                textAlign:
                  letter.direction === "rtl" ? "right" : "center",
                fontSize: "11pt",
                fontWeight: 700,
                marginTop: 0,
                marginBottom: 6,
              }}
            >
              {letter.subject}
            </h4>
          )}

          <div
            dangerouslySetInnerHTML={{ __html: letter.body }}
            style={{ marginTop: 4 }}
          />
        </div>

        {isMD && status === "pending" && company?.signatureStampUrl && (
          <img
            src={company.signatureStampUrl}
            alt="Signature / Stamp"
            crossOrigin="anonymous"
            referrerPolicy="no-referrer"
            draggable={false}
            onMouseDown={handleMouseDown}
            style={{
              position: "absolute",
              left: `${dragPos.x}px`,
              top: `${dragPos.y}px`,
              width: "180px",
              zIndex: 5,
              opacity: 0.95,
              cursor: "grab",
              userSelect: "none",
              pointerEvents: "auto",
            }}
          />
        )}
      </div>

      <MDLetterActions
        status={status}
        letter={letter}
        signing={signing}
        done={done}
        onSign={handleSign}
        isMD={isMD}
      />
    </div>
  );
};

export default MDLetterSign;
